import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { IconComponent } from '../../../../../shared/components/icon/icon.component';
import { ProgressRingComponent } from '../../../../../shared/components/progress-ring/progress-ring.component';
import { SidebarModuleSelectorComponent } from '../sidebar-module-selector/sidebar-module-selector.component';
import { SidebarLessonItemComponent } from '../sidebar-lesson-item/sidebar-lesson-item.component';
import type { CourseContentSidebarLessonItem, CourseContentSidebarModuleItem } from '../../../../../shared/models/interfaces/sidebar.interface';
import { LessonStatus } from '../../../../../shared/models/enums/lesson-status.enum';

@Component({
  selector: 'l-sidebar',
  standalone: true,
  imports: [CommonModule, IconComponent, ProgressRingComponent, SidebarModuleSelectorComponent, SidebarLessonItemComponent],
  templateUrl: './sidebar.component.html',
})
export class SidebarComponent {

  @Input()
  public title: string = 'Conteudo do curso';

  @Input()
  public progress: number = 0;

  @Input()
  public courseProgress: number = 0;

  @Input()
  public lessons: CourseContentSidebarLessonItem[] = [];

  @Input()
  public modules: CourseContentSidebarModuleItem[] = [];

  @Input()
  public selectedModuleId: string = '';

  @Input()
  public selectedLessonId: string = '';

  @Input()
  public hasAssessment: boolean = false;

  @Input()
  public assessmentScoreText: string = '0.0 / 10';

  @Output()
  public lessonSelected: EventEmitter<CourseContentSidebarLessonItem> = new EventEmitter<CourseContentSidebarLessonItem>();

  @Output()
  public assessmentSelected: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  public certificateSelected: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  public certificateDownloadSelected: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  public moduleSelected: EventEmitter<string> = new EventEmitter<string>();

  public isOpen: boolean = true;

  public isModuleDropdownOpen: boolean = false;

  public toggleMenu(): void {
    this.isOpen = !this.isOpen;
    if (!this.isOpen) this.isModuleDropdownOpen = false;
  }

  public toggleModuleDropdown(event: Event): void {
    event.stopPropagation();
    if (this.modules.length <= 1) return;
    this.isModuleDropdownOpen = !this.isModuleDropdownOpen;
  }

  public closeModuleDropdown(): void {
    this.isModuleDropdownOpen = false;
  }

  public onModuleSelected(moduleId: string): void {
    this.isModuleDropdownOpen = false;
    if (moduleId !== this.selectedModuleId) this.moduleSelected.emit(moduleId);
  }

  public get scoreText(): string {
    return this.assessmentScoreText;
  }

  public canAccessLesson(index: number): boolean {
    const hasTrackedProgress = this.lessons.some((lesson) => lesson.status !== LessonStatus.NOT_STARTED);

    if (!hasTrackedProgress) {
      return true;
    }

    return index === 0 || this.lessons[index - 1]?.status === LessonStatus.COMPLETED;
  }

  public canAccessAssessment(): boolean {
    return this.hasAssessment && this.lessons.length > 0 && this.lessons.every((lesson) => lesson.status === LessonStatus.COMPLETED);
  }

  public canAccessCertificate(): boolean {
    return this.courseProgress >= 100;
  }

  public onLessonClick(lesson: CourseContentSidebarLessonItem, index: number): void {
    if (this.canAccessLesson(index)) this.lessonSelected.emit(lesson);
  }

  public onAssessmentClick(): void {
    if (this.canAccessAssessment()) this.assessmentSelected.emit();
  }

  public onCertificateClick(): void {
    if (this.canAccessCertificate()) this.certificateSelected.emit();
  }

  public onCertificateDownloadClick(event: Event): void {
    event.stopPropagation();
    if (this.canAccessCertificate()) this.certificateDownloadSelected.emit();
  }
}
